import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Modal, OverlayTrigger, Pagination, Row, Spinner, Table, Tooltip as BootstrapTooltip } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { FiInfo } from 'react-icons/fi';
import { getUser } from '../utils/auth';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line
} from 'recharts';
import { fileAPI } from '../utils/api';
import { CHART_COLORS } from '../utils/chartColors';
import './DashboardPage.css';

const FINANCIAL_YEAR_START_MONTH_INDEX = 3; // April

const normalizeText = (value) => String(value || '').trim();

const parseAmount = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

  const cleaned = String(value)
    .replace(/,/g, '')
    .replace(/[^\d.-]/g, '');

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

const MONTH_MAP = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11
};

const normalizeTwoDigitYear = (year) => (year < 100 ? year + 2000 : year);

const parseDirectDateValue = (text) => {
  const directDate = new Date(text);
  return Number.isNaN(directDate.getTime()) ? null : directDate;
};

const parseMonthNamedDate = (text) => {
  const monthMatch = text.match(/^(\d{1,2})[-/\s]([A-Za-z]{3})[-/\s](\d{2,4})$/);
  if (!monthMatch) return null;
  const day = Number(monthMatch[1]);
  const month = MONTH_MAP[monthMatch[2].toLowerCase()];
  if (!Number.isInteger(month)) return null;
  const year = normalizeTwoDigitYear(Number(monthMatch[3]));
  const date = new Date(year, month, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const parseNumericDate = (text) => {
  const numericMatch = text.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);
  if (!numericMatch) return null;
  const left = Number(numericMatch[1]);
  const mid = Number(numericMatch[2]);
  const year = normalizeTwoDigitYear(Number(numericMatch[3]));

  // Prefer DD/MM but fallback to MM/DD.
  const ddMm = new Date(year, mid - 1, left);
  if (!Number.isNaN(ddMm.getTime()) && ddMm.getMonth() === mid - 1) return ddMm;
  const mmDd = new Date(year, left - 1, mid);
  return Number.isNaN(mmDd.getTime()) ? null : mmDd;
};

const parseBgDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

  const text = String(value).trim();
  return parseDirectDateValue(text) || parseMonthNamedDate(text) || parseNumericDate(text);
};

const formatDate = (value) => {
  const date = parseBgDate(value);
  if (!date) return '-';
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const formatCurrency = (value) => {
  const number = parseAmount(value);
  if (!Number.isFinite(number)) return 'INR 0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(number);
};

const formatCompactAmount = (value) => {
  const number = parseAmount(value);
  if (!Number.isFinite(number)) return 'INR 0';
  return new Intl.NumberFormat('en-IN', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 2
  }).format(number);
};

const getPercent = (part, total) => {
  if (!total) return 0;
  return (Number(part || 0) / Number(total || 0)) * 100;
};

const getDaysDiff = (targetDate, baseDate = new Date()) => {
  if (!targetDate) return null;
  const base = new Date(baseDate);
  base.setHours(0, 0, 0, 0);
  const target = new Date(targetDate);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - base.getTime()) / (1000 * 60 * 60 * 24));
};

const getFinancialYearWindow = (fyEndYear) => {
  const start = new Date(fyEndYear - 1, FINANCIAL_YEAR_START_MONTH_INDEX, 1, 0, 0, 0, 0);
  const end = new Date(fyEndYear, FINANCIAL_YEAR_START_MONTH_INDEX, 0, 23, 59, 59, 999);
  return { start, end };
};

const getFinancialYearMeta = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const startYear = month >= FINANCIAL_YEAR_START_MONTH_INDEX ? year : year - 1;
  const endYearShort = String(startYear + 1).slice(-2);
  return {
    sortKey: startYear,
    label: `FY${String(startYear).slice(-2)}-${endYearShort}`
  };
};

const getEmptyBgFinalAnalytics = () => ({
  totalAmount: 0,
  totalActiveCount: 0,
  expiringNext30DaysCount: 0,
  expiringNext3MonthsCount: 0,
  expiringFY26Count: 0,
  largestSingleBgAmount: 0,
  exposureBeyondFY27: 0,
  typeExposureData: [],
  projectExposureData: [],
  bankExposureData: [],
  beneficiaryExposureData: [],
  buExposureData: [],
  barByFYCountData: [],
  expiryTimelineByFYAmount: [],
  concentration: {
    topProjectName: '-',
    topProjectPct: 0,
    topBankName: '-',
    topBankPct: 0
  },
  alertSummary: {
    expiring30: 0,
    expiring90: 0,
    expiredUnreleased: 0
  },
  alertRows: []
});

const createBgFinalAccumulator = () => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const next90Days = new Date(now);
  next90Days.setDate(next90Days.getDate() + 90);

  return {
    now,
    next90Days,
    fy26: getFinancialYearWindow(2026),
    fy27: getFinancialYearWindow(2027),
    totalAmount: 0,
    totalActiveCount: 0,
    expiringNext30DaysCount: 0,
    expiringNext3MonthsCount: 0,
    expiringFY26Count: 0,
    largestSingleBgAmount: 0,
    exposureBeyondFY27: 0,
    expiring90Count: 0,
    expiredUnreleasedCount: 0,
    typeAmountMap: {},
    projectAmountMap: {},
    bankAmountMap: {},
    beneficiaryAmountMap: {},
    buAmountMap: {},
    expiryFyAmountMap: {},
    expiryFyCountMap: {},
    alertRows: []
  };
};

const addToNumberMap = (map, key, value) => {
  map[key] = (map[key] || 0) + value;
};

const ensureExpiryFyMeta = (map, fyKey, label) => {
  if (!map[fyKey]) map[fyKey] = { label, value: 0 };
};

const pushBgExpiryAlert = ({ alertRows, row, projectName, validDate, claimDate, daysToValid, reason, severity }) => {
  alertRows.push({
    sNo: row.sNo,
    projectName,
    bgNo: row.bgNo || '-',
    validUpto: validDate,
    claimUpto: claimDate,
    daysToValid,
    reason,
    severity
  });
};

const sortBgFinalAlerts = (alertRows) => alertRows.sort((a, b) => {
  const severityRank = { critical: 1, high: 2, medium: 3, low: 4 };
  if (severityRank[a.severity] !== severityRank[b.severity]) {
    return severityRank[a.severity] - severityRank[b.severity];
  }
  return (a.daysToValid ?? 9999) - (b.daysToValid ?? 9999);
});

const getBgFinalProjectName = (row) => normalizeText(row.eachProject) || normalizeText(row.finalProjects) || 'Unknown';

const buildBgFinalRowFields = (row) => {
  const amount = parseAmount(row.amountInr);
  const validDate = parseBgDate(row.validUpto);
  const claimDate = parseBgDate(row.claimUpto);
  return {
    amount,
    validDate,
    claimDate,
    typeName: normalizeText(row.typeNatureOfBg) || 'Unknown',
    projectName: getBgFinalProjectName(row),
    bankName: normalizeText(row.bank) || 'Unknown',
    beneficiaryName: normalizeText(row.beneficiaryName) || 'Unknown',
    buName: normalizeText(row.bu) || 'Unknown',
    exposureDate: claimDate || validDate
  };
};

const updateBgFinalTotals = ({ acc, amount, typeName, projectName, bankName, beneficiaryName, buName }) => {
  acc.totalAmount += amount;
  if (amount > acc.largestSingleBgAmount) acc.largestSingleBgAmount = amount;
  addToNumberMap(acc.typeAmountMap, typeName, amount);
  addToNumberMap(acc.projectAmountMap, projectName, amount);
  addToNumberMap(acc.bankAmountMap, bankName, amount);
  addToNumberMap(acc.beneficiaryAmountMap, beneficiaryName, amount);
  addToNumberMap(acc.buAmountMap, buName, amount);
};

const updateBgFinalExpiryCounters = ({ acc, daysToValid, daysToClaim, validDate, exposureDate, amount }) => {
  if (daysToValid !== null && daysToValid >= 0) acc.totalActiveCount += 1;
  if (daysToValid !== null && daysToValid >= 0 && daysToValid <= 30) acc.expiringNext30DaysCount += 1;
  if (daysToValid !== null && daysToValid >= 0 && validDate <= acc.next90Days) acc.expiringNext3MonthsCount += 1;
  if (daysToValid !== null && daysToValid >= 0 && daysToValid <= 90) acc.expiring90Count += 1;
  if (validDate >= acc.fy26.start && validDate <= acc.fy26.end) acc.expiringFY26Count += 1;
  if (exposureDate && exposureDate > acc.fy27.end) acc.exposureBeyondFY27 += amount;
  if (daysToValid !== null && daysToValid < 0 && (daysToClaim === null || daysToClaim >= 0)) {
    acc.expiredUnreleasedCount += 1;
  }
};

const pushBgFinalAlertsForRow = ({ acc, row, projectName, validDate, claimDate, daysToValid, daysToClaim }) => {
  if (daysToValid !== null && daysToValid < 0) {
    pushBgExpiryAlert({
      alertRows: acc.alertRows,
      row,
      projectName,
      validDate,
      claimDate,
      daysToValid,
      reason: 'Expired (validity passed)',
      severity: 'high'
    });
  } else if (daysToValid !== null && daysToValid <= 30) {
    pushBgExpiryAlert({
      alertRows: acc.alertRows,
      row,
      projectName,
      validDate,
      claimDate,
      daysToValid,
      reason: 'Expiring within 30 days',
      severity: 'high'
    });
  } else if (daysToValid !== null && daysToValid <= 90) {
    pushBgExpiryAlert({
      alertRows: acc.alertRows,
      row,
      projectName,
      validDate,
      claimDate,
      daysToValid,
      reason: 'Expiring within 90 days',
      severity: 'medium'
    });
  }

  if (daysToValid !== null && daysToValid < 0 && (daysToClaim === null || daysToClaim >= 0)) {
    pushBgExpiryAlert({
      alertRows: acc.alertRows,
      row,
      projectName,
      validDate,
      claimDate,
      daysToValid,
      reason: 'Expired but likely not released',
      severity: 'critical'
    });
  }
};

const updateBgFinalExpiryFyMaps = ({ acc, validDate, amount }) => {
  const fyMeta = getFinancialYearMeta(validDate);
  const fyKey = String(fyMeta.sortKey);
  ensureExpiryFyMeta(acc.expiryFyAmountMap, fyKey, fyMeta.label);
  ensureExpiryFyMeta(acc.expiryFyCountMap, fyKey, fyMeta.label);
  acc.expiryFyAmountMap[fyKey].value += amount;
  acc.expiryFyCountMap[fyKey].value += 1;
};

const addBgFinalRowToAccumulator = (acc, row) => {
  const fields = buildBgFinalRowFields(row);
  updateBgFinalTotals({ acc, ...fields });

  if (!fields.validDate) return;
  const daysToValid = getDaysDiff(fields.validDate, acc.now);
  const daysToClaim = getDaysDiff(fields.claimDate, acc.now);

  updateBgFinalExpiryCounters({
    acc,
    daysToValid,
    daysToClaim,
    validDate: fields.validDate,
    exposureDate: fields.exposureDate,
    amount: fields.amount
  });
  pushBgFinalAlertsForRow({
    acc,
    row,
    projectName: fields.projectName,
    validDate: fields.validDate,
    claimDate: fields.claimDate,
    daysToValid,
    daysToClaim
  });
  updateBgFinalExpiryFyMaps({ acc, validDate: fields.validDate, amount: fields.amount });
};

const buildBgFinalAnalyticsFromAccumulator = (acc) => {
  const typeExposureData = Object.entries(acc.typeAmountMap)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const projectExposureData = Object.entries(acc.projectAmountMap)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  const bankExposureData = Object.entries(acc.bankAmountMap)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  const beneficiaryExposureData = Object.entries(acc.beneficiaryAmountMap)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  const buExposureData = Object.entries(acc.buAmountMap)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const barByFYCountData = Object.entries(acc.expiryFyCountMap)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, meta]) => ({
      fy: meta.label,
      count: meta.value
    }));

  const expiryTimelineByFYAmount = Object.entries(acc.expiryFyAmountMap)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, meta]) => ({
      fy: meta.label,
      amount: meta.value
    }));

  const topProject = projectExposureData[0];
  const topBank = bankExposureData[0];

  return {
    totalAmount: acc.totalAmount,
    totalActiveCount: acc.totalActiveCount,
    expiringNext30DaysCount: acc.expiringNext30DaysCount,
    expiringNext3MonthsCount: acc.expiringNext3MonthsCount,
    expiringFY26Count: acc.expiringFY26Count,
    largestSingleBgAmount: acc.largestSingleBgAmount,
    exposureBeyondFY27: acc.exposureBeyondFY27,
    typeExposureData,
    projectExposureData,
    bankExposureData,
    beneficiaryExposureData,
    buExposureData,
    barByFYCountData,
    expiryTimelineByFYAmount,
    concentration: {
      topProjectName: topProject?.name || '-',
      topProjectPct: getPercent(topProject?.value || 0, acc.totalAmount),
      topBankName: topBank?.name || '-',
      topBankPct: getPercent(topBank?.value || 0, acc.totalAmount)
    },
    alertSummary: {
      expiring30: acc.expiringNext30DaysCount,
      expiring90: acc.expiring90Count,
      expiredUnreleased: acc.expiredUnreleasedCount
    },
    alertRows: sortBgFinalAlerts(acc.alertRows)
  };
};

const computeBgFinalAnalytics = (rows) => {
  if (!rows.length) return getEmptyBgFinalAnalytics();
  const acc = createBgFinalAccumulator();
  rows.forEach((row) => addBgFinalRowToAccumulator(acc, row));
  return buildBgFinalAnalyticsFromAccumulator(acc);
};

const matchesBgFinalActiveFilter = ({ row, activeFilter, now, fy26, fy27 }) => {
  const validDate = parseBgDate(row.validUpto);
  const claimDate = parseBgDate(row.claimUpto);
  const exposureDate = claimDate || validDate;
  const daysToValid = getDaysDiff(validDate, now);
  const claimDays = getDaysDiff(claimDate, now);
  const amount = parseAmount(row.amountInr);
  const rowProject = normalizeText(row.eachProject) || normalizeText(row.finalProjects) || 'Unknown';
  const rowBank = normalizeText(row.bank) || 'Unknown';
  const rowBeneficiary = normalizeText(row.beneficiaryName) || 'Unknown';
  const rowType = normalizeText(row.typeNatureOfBg) || 'Unknown';
  const rowBu = normalizeText(row.bu) || 'Unknown';

  switch (activeFilter.type) {
    case 'all':
      return true;
    case 'largest':
      return amount === parseAmount(activeFilter.value);
    case 'active':
      return daysToValid !== null && daysToValid >= 0;
    case 'expiring30':
      return daysToValid !== null && daysToValid >= 0 && daysToValid <= 30;
    case 'expiring90':
      return daysToValid !== null && daysToValid >= 0 && daysToValid <= 90;
    case 'fy26':
      return validDate && validDate >= fy26.start && validDate <= fy26.end;
    case 'beyondFy27':
      return exposureDate && exposureDate > fy27.end;
    case 'expired':
      return daysToValid !== null && daysToValid < 0 && (claimDays === null || claimDays >= 0);
    case 'project':
      return rowProject === activeFilter.value;
    case 'bank':
      return rowBank === activeFilter.value;
    case 'beneficiary':
      return rowBeneficiary === activeFilter.value;
    case 'type':
      return rowType === activeFilter.value;
    case 'bu':
      return rowBu === activeFilter.value;
    case 'fy':
      return validDate && getFinancialYearMeta(validDate).label === activeFilter.value;
    default:
      return true;
  }
};

const computeBgFinalFilteredRows = ({ rows, activeFilter }) => {
  const fy26 = getFinancialYearWindow(2026);
  const fy27 = getFinancialYearWindow(2027);
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  return rows.filter((row) => matchesBgFinalActiveFilter({ row, activeFilter, now, fy26, fy27 }));
};

const INITIAL_BG_DATA_FINAL_FORM = {
  finalProjects: '',
  purpose: '',
  bu: '',
  eachProject: '',
  beneficiaryName: '',
  bgReferenceNo: '',
  typeNatureOfBg: '',
  issueDate: '',
  bank: '',
  bgNo: '',
  amountInr: '',
  validUpto: '',
  claimUpto: ''
};

const BgActiveFilterPropType = PropTypes.shape({
  type: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.any
});

const BgKpiCardPropType = PropTypes.shape({
  key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  title: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  variant: PropTypes.string,
  valueClassName: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  filterType: PropTypes.string.isRequired,
  filterValue: PropTypes.any
});

const BgAnalyticsPropType = PropTypes.shape({
  projectExposureData: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, value: PropTypes.number })).isRequired,
  bankExposureData: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, value: PropTypes.number })).isRequired,
  typeExposureData: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, value: PropTypes.number })).isRequired,
  buExposureData: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, value: PropTypes.number })).isRequired,
  beneficiaryExposureData: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, value: PropTypes.number })).isRequired,
  expiryTimelineByFYAmount: PropTypes.arrayOf(PropTypes.shape({ fy: PropTypes.string, amount: PropTypes.number })).isRequired,
  concentration: PropTypes.shape({
    topProjectName: PropTypes.string,
    topProjectPct: PropTypes.number,
    topBankName: PropTypes.string,
    topBankPct: PropTypes.number
  }).isRequired,
  alertSummary: PropTypes.shape({
    expiring30: PropTypes.number,
    expiring90: PropTypes.number,
    expiredUnreleased: PropTypes.number
  }).isRequired,
  alertRows: PropTypes.array,
  totalAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  largestSingleBgAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  expiringNext30DaysCount: PropTypes.number,
  expiringNext3MonthsCount: PropTypes.number,
  totalActiveCount: PropTypes.number,
  expiringFY26Count: PropTypes.number,
  exposureBeyondFY27: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
});

const BgActiveFilterBanner = ({ activeFilter, onClear }) => {
  if (activeFilter.type === 'all') return null;

  return (
    <div className="d-flex justify-content-between align-items-center mb-2">
      <div className="d-inline-flex align-items-center gap-2 bg-info-subtle border border-info-subtle text-info-emphasis rounded-pill px-3 py-1 small">
        <strong>Filter:</strong>
        <span>{activeFilter.label}</span>
      </div>
      <Button size="sm" variant="outline-primary" className="py-1 px-2" onClick={onClear}>
        Clear
      </Button>
    </div>
  );
};

BgActiveFilterBanner.propTypes = {
  activeFilter: BgActiveFilterPropType.isRequired,
  onClear: PropTypes.func.isRequired
};

const BgKpiCard = ({ card, isSelected, onSelect }) => (
  <Card
    className={`stat-card ${card.variant} shadow-sm h-100 ${isSelected ? 'border border-primary border-2' : ''}`}
    style={{ cursor: 'pointer' }}
    onClick={onSelect}
  >
    <Card.Body>
      <p className="stat-title d-flex align-items-center gap-1">
        {card.title}
        <OverlayTrigger placement="top" overlay={<BootstrapTooltip id={`kpi-tip-${card.key}`}>{card.tooltip}</BootstrapTooltip>}>
          <button
            type="button"
            className="btn btn-link p-0 border-0 ms-1 d-inline-flex align-items-center justify-content-center"
            aria-label={`Info about ${card.title}`}
            onClick={(e) => e.stopPropagation()}
            style={{ lineHeight: 1 }}
          >
            <span
              className="rounded-circle border text-secondary d-inline-flex align-items-center justify-content-center"
              style={{ width: 16, height: 16 }}
            >
              <FiInfo size={10} />
            </span>
          </button>
        </OverlayTrigger>
      </p>
      <h3 className={`stat-value ${card.valueClassName || ''}`.trim()}>{card.value}</h3>
    </Card.Body>
  </Card>
);

BgKpiCard.propTypes = {
  card: BgKpiCardPropType.isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired
};

const BgKpiCardGrid = ({ cards, activeFilter, onApplyFilter, className }) => (
  <Row className={className}>
    {cards.map((card) => {
      const isSelected = activeFilter.type === card.filterType && String(activeFilter.value) === String(card.filterValue);
      return (
        <Col key={card.key}>
          <BgKpiCard
            card={card}
            isSelected={isSelected}
            onSelect={() => onApplyFilter(card.filterType, card.filterValue, card.title)}
          />
        </Col>
      );
    })}
  </Row>
);

BgKpiCardGrid.propTypes = {
  className: PropTypes.string,
  cards: PropTypes.arrayOf(BgKpiCardPropType).isRequired,
  activeFilter: BgActiveFilterPropType.isRequired,
  onApplyFilter: PropTypes.func.isRequired
};

const BgProjectAndBankCharts = ({ analytics, onApplyFilter }) => (
  <Row className="g-3 mb-3">
    <Col lg={6}>
      <Card className="shadow-sm h-100">
        <Card.Header className="bg-white border-bottom">
          <h6 className="mb-0">Project-wise Exposure (Top 10)</h6>
        </Card.Header>
        <Card.Body style={{ minHeight: 330 }}>
          {analytics.projectExposureData.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={analytics.projectExposureData}
                layout="vertical"
                margin={{ top: 10, right: 20, left: 90, bottom: 10 }}
                onClick={(state) => {
                  const name = state?.activeLabel;
                  if (name) onApplyFilter('project', name, `Project: ${name}`);
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(value) => formatCompactAmount(value)} />
                <YAxis type="category" dataKey="name" width={140} />
                <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="value" fill="#667eea" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted mb-0">No data available.</p>
          )}
        </Card.Body>
      </Card>
    </Col>
    <Col lg={6}>
      <Card className="shadow-sm h-100">
        <Card.Header className="bg-white border-bottom">
          <h6 className="mb-0">Bank-wise Exposure (Top 10)</h6>
        </Card.Header>
        <Card.Body style={{ minHeight: 330 }}>
          {analytics.bankExposureData.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={analytics.bankExposureData}
                margin={{ top: 10, right: 20, left: 0, bottom: 70 }}
                onClick={(state) => {
                  const name = state?.activeLabel;
                  if (name) onApplyFilter('bank', name, `Bank: ${name}`);
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" interval={0} angle={-30} textAnchor="end" height={70} />
                <YAxis tickFormatter={(value) => formatCompactAmount(value)} />
                <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="value" fill="#2563eb" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted mb-0">No data available.</p>
          )}
        </Card.Body>
      </Card>
    </Col>
  </Row>
);

BgProjectAndBankCharts.propTypes = {
  analytics: BgAnalyticsPropType.isRequired,
  onApplyFilter: PropTypes.func.isRequired
};

const BgTypeAndBuCharts = ({ analytics, onApplyFilter }) => (
  <Row className="g-3 mb-3">
    <Col lg={6}>
      <Card className="shadow-sm h-100">
        <Card.Header className="bg-white border-bottom">
          <h6 className="mb-0">Type-wise Exposure</h6>
        </Card.Header>
        <Card.Body style={{ minHeight: 340 }}>
          {analytics.typeExposureData.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={analytics.typeExposureData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ name, percent }) => `${name} (${((percent || 0) * 100).toFixed(0)}%)`}
                  onClick={(_, index) => {
                    const item = analytics.typeExposureData[index];
                    if (item?.name) onApplyFilter('type', item.name, `Type: ${item.name}`);
                  }}
                >
                  {analytics.typeExposureData.map((entry, index) => (
                    <Cell key={`${entry.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted mb-0">No data available.</p>
          )}
        </Card.Body>
      </Card>
    </Col>
    <Col lg={6}>
      <Card className="shadow-sm h-100">
        <Card.Header className="bg-white border-bottom">
          <h6 className="mb-0">BU-wise Exposure</h6>
        </Card.Header>
        <Card.Body style={{ minHeight: 340 }}>
          {analytics.buExposureData.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={analytics.buExposureData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ name, percent }) => `${name} (${((percent || 0) * 100).toFixed(0)}%)`}
                  onClick={(_, index) => {
                    const item = analytics.buExposureData[index];
                    if (item?.name) onApplyFilter('bu', item.name, `BU: ${item.name}`);
                  }}
                >
                  {analytics.buExposureData.map((entry, index) => (
                    <Cell key={`${entry.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted mb-0">No data available.</p>
          )}
        </Card.Body>
      </Card>
    </Col>
  </Row>
);

BgTypeAndBuCharts.propTypes = {
  analytics: BgAnalyticsPropType.isRequired,
  onApplyFilter: PropTypes.func.isRequired
};

const BgExpiryAndConcentration = ({ analytics, onApplyFilter }) => (
  <Row className="g-3 mb-3">
    <Col lg={8}>
      <Card className="shadow-sm">
        <Card.Header className="bg-white border-bottom">
          <h6 className="mb-0">Expiry Timeline (FY-wise Amount Forecast)</h6>
        </Card.Header>
        <Card.Body style={{ minHeight: 340 }}>
          {analytics.expiryTimelineByFYAmount.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={analytics.expiryTimelineByFYAmount}
                onClick={(state) => {
                  const fy = state?.activeLabel;
                  if (fy) onApplyFilter('fy', fy, `Financial Year: ${fy}`);
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="fy" />
                <YAxis tickFormatter={(value) => formatCompactAmount(value)} />
                <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Line type="monotone" dataKey="amount" name="Total Amount Expiring" stroke="#16a34a" strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted mb-0">No expiry timeline data available.</p>
          )}
        </Card.Body>
      </Card>
    </Col>
    <Col lg={4}>
      <Card className="shadow-sm h-100">
        <Card.Header className="bg-white border-bottom">
          <h6 className="mb-0">Concentration Risk</h6>
        </Card.Header>
        <Card.Body>
          <p className="mb-2"><strong>Top Project:</strong> {analytics.concentration.topProjectName}</p>
          <p className="mb-3 text-danger">
            {analytics.concentration.topProjectPct.toFixed(1)}% of total exposure
          </p>
          <p className="mb-2"><strong>Top Bank:</strong> {analytics.concentration.topBankName}</p>
          <p className="mb-0 text-warning">
            {analytics.concentration.topBankPct.toFixed(1)}% of total exposure
          </p>
        </Card.Body>
      </Card>
    </Col>
  </Row>
);

BgExpiryAndConcentration.propTypes = {
  analytics: BgAnalyticsPropType.isRequired,
  onApplyFilter: PropTypes.func.isRequired
};

const BgBeneficiaryAndAlerts = ({ analytics, filteredAlertRows, activeFilter, onApplyFilter }) => (
  <Row className="g-3 mb-3">
    <Col lg={6}>
      <Card className="shadow-sm h-100">
        <Card.Header className="bg-white border-bottom">
          <h6 className="mb-0">Beneficiary-wise Exposure (Top 10)</h6>
        </Card.Header>
        <Card.Body style={{ minHeight: 340 }}>
          {analytics.beneficiaryExposureData.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={analytics.beneficiaryExposureData}
                layout="vertical"
                margin={{ top: 10, right: 20, left: 90, bottom: 10 }}
                onClick={(state) => {
                  const name = state?.activeLabel;
                  if (name) onApplyFilter('beneficiary', name, `Beneficiary: ${name}`);
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(value) => formatCompactAmount(value)} />
                <YAxis type="category" dataKey="name" width={160} />
                <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="value" fill="#9333ea" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted mb-0">No data available.</p>
          )}
        </Card.Body>
      </Card>
    </Col>
    <Col lg={6}>
      <Card className="shadow-sm h-100">
        <Card.Header className="bg-white border-bottom">
          <h6 className="mb-0">Alert Panel</h6>
        </Card.Header>
        <Card.Body>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <span
              className={`badge ${activeFilter.type === 'expiring30' ? 'bg-danger border border-2 border-dark' : 'bg-danger-subtle text-danger border border-danger-subtle'}`}
              style={{ cursor: 'pointer' }}
              onClick={() => onApplyFilter('expiring30', null, 'Expiring <30 days')}
            >
              Expiring &lt;30d: {analytics.alertSummary.expiring30}
            </span>
            <span
              className={`badge ${activeFilter.type === 'expiring90' ? 'bg-warning text-dark border border-2 border-dark' : 'bg-warning-subtle text-warning-emphasis border border-warning-subtle'}`}
              style={{ cursor: 'pointer' }}
              onClick={() => onApplyFilter('expiring90', null, 'Expiring <90 days')}
            >
              Expiring &lt;90d: {analytics.alertSummary.expiring90}
            </span>
            <span
              className={`badge ${activeFilter.type === 'expired' ? 'bg-dark border border-2 border-warning' : 'bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle'}`}
              style={{ cursor: 'pointer' }}
              onClick={() => onApplyFilter('expired', null, 'Expired not released')}
            >
              Expired: {analytics.alertSummary.expiredUnreleased}
            </span>
          </div>
          {filteredAlertRows.length ? (
            <div className="table-responsive" style={{ maxHeight: 260 }}>
              <Table size="sm" bordered hover className="mb-0">
                <thead>
                  <tr>
                    <th>S No</th>
                    <th>Project</th>
                    <th>BG No</th>
                    <th>Valid Upto</th>
                    <th>Claim Upto</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAlertRows.map((item, idx) => (
                    <tr key={`${item.sNo}-${idx}`}>
                      <td>{item.sNo}</td>
                      <td>{item.projectName}</td>
                      <td>{item.bgNo}</td>
                      <td>{formatDate(item.validUpto)}</td>
                      <td>{formatDate(item.claimUpto)}</td>
                      <td>{item.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <p className="text-muted mb-0">No active alerts.</p>
          )}
        </Card.Body>
      </Card>
    </Col>
  </Row>
);

BgBeneficiaryAndAlerts.propTypes = {
  analytics: BgAnalyticsPropType.isRequired,
  filteredAlertRows: PropTypes.arrayOf(PropTypes.shape({
    sNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    projectName: PropTypes.string,
    bgNo: PropTypes.string,
    validUpto: PropTypes.any,
    claimUpto: PropTypes.any,
    reason: PropTypes.string
  })).isRequired,
  activeFilter: BgActiveFilterPropType.isRequired,
  onApplyFilter: PropTypes.func.isRequired
};

const BgAddEntryCard = ({
  showAddForm,
  setShowAddForm,
  addFormError,
  addFormSuccess,
  addFormData,
  onChangeField,
  onSubmit,
  addFormSubmitting,
  onResetMessages,
  editingSNo,
  onCancelEdit,
  addFormFiles,
  onChangeFormFile,
  existingDocsForEditing,
  onViewExistingDoc
}) => {
  const isEditing = editingSNo !== null && editingSNo !== undefined;

  return (
    <Card className="shadow-sm mb-3">
      <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          {isEditing ? `Edit BG Data Final List Entry (S No: ${editingSNo})` : 'Add BG Data Final List Entry'}
        </h5>
        <Button
          size="sm"
          variant={showAddForm ? 'outline-secondary' : 'primary'}
          onClick={() => {
            if (isEditing) {
              onCancelEdit();
            } else {
              setShowAddForm((prev) => !prev);
              onResetMessages();
            }
          }}
        >
          {showAddForm ? (isEditing ? 'Cancel Edit' : 'Hide Form') : 'Add Entry'}
        </Button>
      </Card.Header>
      {showAddForm && (
        <Card.Body>
          {addFormError && <Alert variant="danger" className="mb-3">{addFormError}</Alert>}
          {addFormSuccess && <Alert variant="success" className="mb-3">{addFormSuccess}</Alert>}
          <Form onSubmit={onSubmit}>
          <Row className="g-3">
            <Col md={4}>
              <Form.Label className="mb-1">Final Projects</Form.Label>
              <Form.Control value={addFormData.finalProjects} onChange={(e) => onChangeField('finalProjects', e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">Purpose</Form.Label>
              <Form.Control value={addFormData.purpose} onChange={(e) => onChangeField('purpose', e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">BU</Form.Label>
              <Form.Control value={addFormData.bu} onChange={(e) => onChangeField('bu', e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">Each Project</Form.Label>
              <Form.Control value={addFormData.eachProject} onChange={(e) => onChangeField('eachProject', e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">Beneficiary Name</Form.Label>
              <Form.Control value={addFormData.beneficiaryName} onChange={(e) => onChangeField('beneficiaryName', e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">BG Reference No</Form.Label>
              <Form.Control value={addFormData.bgReferenceNo} onChange={(e) => onChangeField('bgReferenceNo', e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">Type/Nature of BG</Form.Label>
              <Form.Control value={addFormData.typeNatureOfBg} onChange={(e) => onChangeField('typeNatureOfBg', e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">Issue Date</Form.Label>
              <Form.Control type="date" value={addFormData.issueDate} onChange={(e) => onChangeField('issueDate', e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">Bank</Form.Label>
              <Form.Control value={addFormData.bank} onChange={(e) => onChangeField('bank', e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">BG No</Form.Label>
              <Form.Control value={addFormData.bgNo} onChange={(e) => onChangeField('bgNo', e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">Amount (INR)</Form.Label>
              <Form.Control type="number" step="0.01" value={addFormData.amountInr} onChange={(e) => onChangeField('amountInr', e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">Valid Upto</Form.Label>
              <Form.Control type="date" value={addFormData.validUpto} onChange={(e) => onChangeField('validUpto', e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">Claim Upto</Form.Label>
              <Form.Control type="date" value={addFormData.claimUpto} onChange={(e) => onChangeField('claimUpto', e.target.value)} />
            </Col>
          </Row>

          <hr />
          <div className="mb-2 fw-semibold">Documents</div>
          <Row className="g-3">
            {DOC_SLOTS.map((slot) => {
              const existing = existingDocsForEditing?.[slot - 1] || null;
              return (
                <Col md={4} key={slot}>
                  <Form.Label className="mb-1">Doc {slot}</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/png,image/jpeg,application/pdf"
                    onChange={(e) => onChangeFormFile(slot, e.target.files?.[0] || null)}
                  />
                  {existing && (
                    <div className="small text-muted mt-1">
                      Current: {existing.original_name || `Doc ${slot}`}{' '}
                      <Button variant="link" size="sm" className="p-0 align-baseline" onClick={() => onViewExistingDoc(existing)}>View</Button>
                      {' '}(choosing a file above replaces it)
                    </div>
                  )}
                  {addFormFiles?.[slot - 1] && (
                    <div className="small text-success mt-1">Selected: {addFormFiles[slot - 1].name}</div>
                  )}
                </Col>
              );
            })}
          </Row>

          <div className="mt-3 d-flex justify-content-end">
            <Button type="submit" disabled={addFormSubmitting}>
              {addFormSubmitting ? 'Saving...' : (isEditing ? 'Update Entry' : 'Save Entry')}
            </Button>
          </div>
        </Form>
        </Card.Body>
      )}
    </Card>
  );
};

const BgAddFormDataPropType = PropTypes.shape({
  finalProjects: PropTypes.string,
  purpose: PropTypes.string,
  bu: PropTypes.string,
  eachProject: PropTypes.string,
  beneficiaryName: PropTypes.string,
  bgReferenceNo: PropTypes.string,
  typeNatureOfBg: PropTypes.string,
  issueDate: PropTypes.string,
  bank: PropTypes.string,
  bgNo: PropTypes.string,
  amountInr: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  validUpto: PropTypes.string,
  claimUpto: PropTypes.string
});

BgAddEntryCard.propTypes = {
  showAddForm: PropTypes.bool.isRequired,
  setShowAddForm: PropTypes.func.isRequired,
  addFormError: PropTypes.string,
  addFormSuccess: PropTypes.string,
  addFormData: BgAddFormDataPropType.isRequired,
  onChangeField: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  addFormSubmitting: PropTypes.bool.isRequired,
  onResetMessages: PropTypes.func.isRequired,
  editingSNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCancelEdit: PropTypes.func.isRequired,
  addFormFiles: PropTypes.array,
  onChangeFormFile: PropTypes.func.isRequired,
  existingDocsForEditing: PropTypes.array,
  onViewExistingDoc: PropTypes.func.isRequired
};

BgAddEntryCard.defaultProps = {
  addFormError: '',
  addFormSuccess: '',
  editingSNo: null,
  addFormFiles: [null, null, null, null, null],
  existingDocsForEditing: [null, null, null, null, null]
};

const DOC_SLOTS = [1, 2, 3, 4, 5];

// One cell = one upload slot for one row. Self-contained (owns its own hidden
// file input) so the table doesn't need to track a ref per row x slot.
const BgDocCell = ({ sNo, slot, attachment, canManage, onUpload, onView, onRemove }) => {
  const inputRef = React.useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file next time
    if (file) onUpload(sNo, slot, file);
  };

  return (
    <div className="d-flex align-items-center gap-1">
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,application/pdf"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      {attachment ? (
        <>
          <Button size="sm" variant="outline-primary" onClick={() => onView(attachment)}>View</Button>
          {canManage && (
            <>
              <Button size="sm" variant="outline-secondary" title="Replace" onClick={() => inputRef.current?.click()}>↻</Button>
              <Button size="sm" variant="outline-danger" title="Remove" onClick={() => onRemove(attachment.id)}>×</Button>
            </>
          )}
        </>
      ) : canManage ? (
        <Button size="sm" variant="outline-secondary" onClick={() => inputRef.current?.click()}>Upload</Button>
      ) : (
        <span className="text-muted small">—</span>
      )}
    </div>
  );
};

BgDocCell.propTypes = {
  sNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  slot: PropTypes.number.isRequired,
  attachment: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    original_name: PropTypes.string,
    mime_type: PropTypes.string
  }),
  canManage: PropTypes.bool.isRequired,
  onUpload: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
};

BgDocCell.defaultProps = {
  attachment: null
};

const BgDataTableCard = ({
  rows,
  paginatedRows,
  currentPageSafe,
  pageSize,
  filteredCount,
  totalPages,
  onSetPageSize,
  onSetCurrentPage,
  attachmentsBySNo,
  canManageDocs,
  onUploadDoc,
  onViewDoc,
  onRemoveDoc,
  canManageRows,
  onEditRow,
  onDeleteRow
}) => (
  <Card className="shadow-sm">
    <Card.Header className="bg-white border-bottom">
      <h5 className="mb-0">BG Data Final List</h5>
    </Card.Header>
    <Card.Body>
      {!rows.length ? (
        <p className="text-muted mb-0">No BG Data Final List records found.</p>
      ) : (
        <>
          <div className="table-responsive">
            <Table striped bordered hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>S No</th>
                  <th>Final Projects</th>
                  <th>Purpose</th>
                  <th>BU</th>
                  <th>Each Project</th>
                  <th>Beneficiary Name</th>
                  <th>BG Reference No/Tender/Contract/PO No</th>
                  <th>Type/Nature of BG</th>
                  <th>Issue Date</th>
                  <th>Bank</th>
                  <th>BG No</th>
                  <th>Amount (INR)</th>
                  <th>Valid Upto</th>
                  <th>Claim Upto</th>
                  {DOC_SLOTS.map((slot) => <th key={slot}>Doc {slot}</th>)}
                  {canManageRows && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((row, index) => (
                  <tr key={`${row.sNo}-${index}`}>
                    <td>{row.sNo ?? ((currentPageSafe - 1) * pageSize + index + 1)}</td>
                    <td>{row.finalProjects || '-'}</td>
                    <td>{row.purpose || '-'}</td>
                    <td>{row.bu || '-'}</td>
                    <td>{row.eachProject || '-'}</td>
                    <td>{row.beneficiaryName || '-'}</td>
                    <td>{row.bgReferenceNo || '-'}</td>
                    <td>{row.typeNatureOfBg || '-'}</td>
                    <td>{formatDate(row.issueDate)}</td>
                    <td>{row.bank || '-'}</td>
                    <td>{row.bgNo || '-'}</td>
                    <td>{formatCurrency(row.amountInr)}</td>
                    <td>{formatDate(row.validUpto)}</td>
                    <td>{formatDate(row.claimUpto)}</td>
                    {DOC_SLOTS.map((slot) => (
                      <td key={slot} className="text-nowrap">
                        <BgDocCell
                          sNo={row.sNo}
                          slot={slot}
                          attachment={attachmentsBySNo?.[row.sNo]?.[slot - 1] || null}
                          canManage={canManageDocs}
                          onUpload={onUploadDoc}
                          onView={onViewDoc}
                          onRemove={onRemoveDoc}
                        />
                      </td>
                    ))}
                    {canManageRows && (
                      <td className="text-nowrap">
                        <div className="d-flex gap-1">
                          <Button size="sm" variant="outline-primary" onClick={() => onEditRow(row)}>Edit</Button>
                          <Button size="sm" variant="outline-danger" onClick={() => onDeleteRow(row.sNo)}>Delete</Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              <small className="text-muted">
                Showing {(currentPageSafe - 1) * pageSize + 1}-{Math.min(currentPageSafe * pageSize, filteredCount)} of {filteredCount} (Total {rows.length})
              </small>
              <div className="d-flex align-items-center gap-2">
                <small className="text-muted">Rows per page</small>
                <Form.Select
                  size="sm"
                  style={{ width: 90 }}
                  value={pageSize}
                  onChange={(e) => onSetPageSize(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Form.Select>
              </div>
            </div>
            <Pagination size="sm" className="mb-0">
              <Pagination.First onClick={() => onSetCurrentPage(1)} disabled={currentPageSafe <= 1} />
              <Pagination.Prev onClick={() => onSetCurrentPage((prev) => Math.max(1, prev - 1))} disabled={currentPageSafe <= 1} />
              <Pagination.Item active>{currentPageSafe}</Pagination.Item>
              <Pagination.Item disabled>{`/ ${totalPages}`}</Pagination.Item>
              <Pagination.Next onClick={() => onSetCurrentPage((prev) => Math.min(totalPages, prev + 1))} disabled={currentPageSafe >= totalPages} />
              <Pagination.Last onClick={() => onSetCurrentPage(totalPages)} disabled={currentPageSafe >= totalPages} />
            </Pagination>
          </div>
        </>
      )}
    </Card.Body>
  </Card>
);

const BgRowPropType = PropTypes.shape({
  sNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  finalProjects: PropTypes.string,
  purpose: PropTypes.string,
  bu: PropTypes.string,
  eachProject: PropTypes.string,
  beneficiaryName: PropTypes.string,
  bgReferenceNo: PropTypes.string,
  typeNatureOfBg: PropTypes.string,
  issueDate: PropTypes.any,
  bank: PropTypes.string,
  bgNo: PropTypes.string,
  amountInr: PropTypes.any,
  validUpto: PropTypes.any,
  claimUpto: PropTypes.any
});

BgDataTableCard.propTypes = {
  rows: PropTypes.arrayOf(BgRowPropType).isRequired,
  paginatedRows: PropTypes.arrayOf(BgRowPropType).isRequired,
  currentPageSafe: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  filteredCount: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onSetPageSize: PropTypes.func.isRequired,
  onSetCurrentPage: PropTypes.func.isRequired,
  attachmentsBySNo: PropTypes.object,
  canManageDocs: PropTypes.bool.isRequired,
  onUploadDoc: PropTypes.func.isRequired,
  onViewDoc: PropTypes.func.isRequired,
  onRemoveDoc: PropTypes.func.isRequired,
  canManageRows: PropTypes.bool.isRequired,
  onEditRow: PropTypes.func.isRequired,
  onDeleteRow: PropTypes.func.isRequired
};

BgDataTableCard.defaultProps = {
  attachmentsBySNo: {}
};

const BGDataFinalListDashboardView = ({
  error,
  canSeePage,
  activeFilter,
  onClearFilter,
  kpiCards,
  onApplyFilter,
  analytics,
  filteredAlertRows,
  rows,
  paginatedRows,
  currentPageSafe,
  pageSize,
  filteredRowsCount,
  totalPages,
  onSetPageSize,
  onSetCurrentPage,
  showAddForm,
  setShowAddForm,
  addFormError,
  addFormSuccess,
  addFormData,
  onChangeAddFormField,
  onSubmitAddForm,
  addFormSubmitting,
  onResetAddFormMessages,
  attachmentsBySNo,
  canManageDocs,
  docActionError,
  onUploadDoc,
  onViewDoc,
  onRemoveDoc,
  editingSNo,
  onCancelEdit,
  addFormFiles,
  onChangeFormFile,
  existingDocsForEditing,
  canManageRows,
  onEditRow,
  onDeleteRow,
  onOpenImportExcel,
  canImportExcel
}) => (
  <Container fluid className="dashboard-page py-4">
    {error && <Alert variant="danger">{error}</Alert>}
    {docActionError && <Alert variant="danger">{docActionError}</Alert>}

    {!canSeePage ? (
      <Alert variant="warning">BG Data Final List is available only for Finance users and EXCO.</Alert>
    ) : (
      <>
        <BgActiveFilterBanner activeFilter={activeFilter} onClear={onClearFilter} />

        <BgKpiCardGrid
          cards={kpiCards.slice(0, 5)}
          activeFilter={activeFilter}
          onApplyFilter={onApplyFilter}
          className="g-3 mb-3 row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-5"
        />

        <BgKpiCardGrid
          cards={kpiCards.slice(5)}
          activeFilter={activeFilter}
          onApplyFilter={onApplyFilter}
          className="g-3 mb-3 row-cols-1 row-cols-md-2 row-cols-lg-3"
        />

        <BgProjectAndBankCharts analytics={analytics} onApplyFilter={onApplyFilter} />
        <BgTypeAndBuCharts analytics={analytics} onApplyFilter={onApplyFilter} />
        <BgExpiryAndConcentration analytics={analytics} onApplyFilter={onApplyFilter} />
        <BgBeneficiaryAndAlerts
          analytics={analytics}
          filteredAlertRows={filteredAlertRows}
          activeFilter={activeFilter}
          onApplyFilter={onApplyFilter}
        />

        {canImportExcel && (
          <div className="d-flex justify-content-end mb-2">
            <Button variant="outline-primary" onClick={onOpenImportExcel}>
              Import Excel
            </Button>
          </div>
        )}

        <BgAddEntryCard
          showAddForm={showAddForm}
          setShowAddForm={setShowAddForm}
          addFormError={addFormError}
          addFormSuccess={addFormSuccess}
          addFormData={addFormData}
          onChangeField={onChangeAddFormField}
          onSubmit={onSubmitAddForm}
          addFormSubmitting={addFormSubmitting}
          onResetMessages={onResetAddFormMessages}
          editingSNo={editingSNo}
          onCancelEdit={onCancelEdit}
          addFormFiles={addFormFiles}
          onChangeFormFile={onChangeFormFile}
          existingDocsForEditing={existingDocsForEditing}
          onViewExistingDoc={onViewDoc}
        />

        <BgDataTableCard
          rows={rows}
          paginatedRows={paginatedRows}
          currentPageSafe={currentPageSafe}
          pageSize={pageSize}
          filteredCount={filteredRowsCount}
          totalPages={totalPages}
          onSetPageSize={onSetPageSize}
          onSetCurrentPage={onSetCurrentPage}
          attachmentsBySNo={attachmentsBySNo}
          canManageDocs={canManageDocs}
          onUploadDoc={onUploadDoc}
          onViewDoc={onViewDoc}
          onRemoveDoc={onRemoveDoc}
          canManageRows={canManageRows}
          onEditRow={onEditRow}
          onDeleteRow={onDeleteRow}
        />
      </>
    )}
  </Container>
);

BGDataFinalListDashboardView.propTypes = {
  error: PropTypes.string,
  canSeePage: PropTypes.bool.isRequired,
  activeFilter: BgActiveFilterPropType.isRequired,
  onClearFilter: PropTypes.func.isRequired,
  kpiCards: PropTypes.arrayOf(BgKpiCardPropType).isRequired,
  onApplyFilter: PropTypes.func.isRequired,
  analytics: BgAnalyticsPropType.isRequired,
  filteredAlertRows: PropTypes.array.isRequired,
  rows: PropTypes.arrayOf(BgRowPropType).isRequired,
  paginatedRows: PropTypes.arrayOf(BgRowPropType).isRequired,
  currentPageSafe: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  filteredRowsCount: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onSetPageSize: PropTypes.func.isRequired,
  onSetCurrentPage: PropTypes.func.isRequired,
  showAddForm: PropTypes.bool.isRequired,
  setShowAddForm: PropTypes.func.isRequired,
  addFormError: PropTypes.string,
  addFormSuccess: PropTypes.string,
  addFormData: BgAddFormDataPropType.isRequired,
  onChangeAddFormField: PropTypes.func.isRequired,
  onSubmitAddForm: PropTypes.func.isRequired,
  addFormSubmitting: PropTypes.bool.isRequired,
  onResetAddFormMessages: PropTypes.func.isRequired,
  attachmentsBySNo: PropTypes.object,
  canManageDocs: PropTypes.bool.isRequired,
  docActionError: PropTypes.string,
  onUploadDoc: PropTypes.func.isRequired,
  onViewDoc: PropTypes.func.isRequired,
  onRemoveDoc: PropTypes.func.isRequired,
  editingSNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCancelEdit: PropTypes.func.isRequired,
  addFormFiles: PropTypes.array,
  onChangeFormFile: PropTypes.func.isRequired,
  existingDocsForEditing: PropTypes.array,
  canManageRows: PropTypes.bool.isRequired,
  onEditRow: PropTypes.func.isRequired,
  onDeleteRow: PropTypes.func.isRequired,
  onOpenImportExcel: PropTypes.func.isRequired,
  canImportExcel: PropTypes.bool.isRequired
};

BGDataFinalListDashboardView.defaultProps = {
  attachmentsBySNo: {},
  editingSNo: null,
  addFormFiles: [null, null, null, null, null],
  existingDocsForEditing: [null, null, null, null, null]
};

const BGDataFinalListDashboardPage = ({ departmentId = null }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [canSeePage, setCanSeePage] = useState(false);
  const [activeFilter, setActiveFilter] = useState({ type: 'all', value: null, label: 'All Records' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addFormSubmitting, setAddFormSubmitting] = useState(false);
  const [addFormError, setAddFormError] = useState('');
  const [addFormSuccess, setAddFormSuccess] = useState('');
  const [addFormData, setAddFormData] = useState(INITIAL_BG_DATA_FINAL_FORM);

  const roleType = String(getUser()?.roleType || getUser()?.role?.roleType || '').toUpperCase();
  const canManageDocs = ['ADMIN', 'EDITOR', 'EXCO'].includes(roleType);
  // Editing/deleting the row's own fields is narrower than managing its
  // documents — matches the ADMIN-only convention already used by the
  // sibling Claim MIS / Insurance MIS edit-row features.
  const canManageRows = roleType === 'ADMIN';
  // Bulk Excel import is granted to EDITOR too (matches the route's
  // requireRoleTypes('ADMIN', 'EDITOR')) — broader than single-row
  // Edit/Delete, which stays ADMIN-only.
  const canImportExcel = roleType === 'ADMIN' || roleType === 'EDITOR';

  const [editingSNo, setEditingSNo] = useState(null);
  const [addFormFiles, setAddFormFiles] = useState([null, null, null, null, null]);

  // Bulk Excel import modal state (ADMIN only)
  const [showImportExcel, setShowImportExcel] = useState(false);
  const [importExcelFile, setImportExcelFile] = useState(null);
  const [importExcelSubmitting, setImportExcelSubmitting] = useState(false);
  const [importExcelResult, setImportExcelResult] = useState(null);
  const [importExcelError, setImportExcelError] = useState('');

  const [attachmentsBySNo, setAttachmentsBySNo] = useState({});
  const [docActionError, setDocActionError] = useState('');
  const [showDocPreview, setShowDocPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewName, setPreviewName] = useState('');
  const [previewMime, setPreviewMime] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const analytics = useMemo(() => computeBgFinalAnalytics(rows), [rows]);

  const kpiCards = useMemo(() => ([
    {
      key: 'total-amount',
      title: 'Total BG Amount (INR)',
      value: formatCurrency(analytics.totalAmount),
      variant: 'stat-primary',
      valueClassName: 'stat-value-long',
      filterType: 'all',
      filterValue: null,
      tooltip: 'Sum of amount_inr across all BG rows.'
    },
    {
      key: 'largest-bg',
      title: 'Largest Single BG',
      value: formatCurrency(analytics.largestSingleBgAmount),
      variant: 'stat-warning',
      valueClassName: 'stat-value-long',
      filterType: 'largest',
      filterValue: analytics.largestSingleBgAmount,
      tooltip: 'Maximum amount_inr in a single BG row.'
    },
    {
      key: 'expiring-30',
      title: 'Expiring in Next 30 Days',
      value: analytics.expiringNext30DaysCount,
      variant: 'stat-danger',
      filterType: 'expiring30',
      filterValue: null,
      tooltip: 'valid_upto between today and today + 30 days (inclusive).'
    },
    {
      key: 'expiring-90',
      title: 'BGs Expiring in Next 3 Months',
      value: analytics.expiringNext3MonthsCount,
      variant: 'stat-info',
      filterType: 'expiring90',
      filterValue: null,
      tooltip: 'valid_upto between today and today + 90 days (inclusive).'
    },
    {
      key: 'active-count',
      title: 'Total Active BG Count',
      value: analytics.totalActiveCount,
      variant: 'stat-success',
      filterType: 'active',
      filterValue: null,
      tooltip: 'Count of rows where valid_upto is today or in future.'
    },
    {
      key: 'expiring-fy26',
      title: 'BGs Expiring in FY26',
      value: analytics.expiringFY26Count,
      variant: 'stat-secondary',
      filterType: 'fy26',
      filterValue: null,
      tooltip: 'valid_upto between 01-Apr-2025 and 31-Mar-2026.'
    },
    {
      key: 'exposure-fy27',
      title: 'Total Exposure Beyond FY27',
      value: formatCurrency(analytics.exposureBeyondFY27),
      variant: 'stat-primary',
      valueClassName: 'stat-value-long',
      filterType: 'beyondFy27',
      filterValue: null,
      tooltip: 'Sum of amount_inr where exposure date (claim_upto else valid_upto) is after 31-Mar-2027.'
    }
  ]), [analytics]);

  const applyFilter = (type, value, label) => {
    setActiveFilter({ type, value, label });
  };

  const clearFilter = () => {
    setActiveFilter({ type: 'all', value: null, label: 'All Records' });
  };

  const filteredRows = useMemo(() => computeBgFinalFilteredRows({ rows, activeFilter }), [rows, activeFilter]);

  const filteredAlertRows = useMemo(() => {
    if (activeFilter.type === 'all') return analytics.alertRows;
    const allowedSno = new Set(filteredRows.map((row) => row.sNo));
    return analytics.alertRows.filter((item) => allowedSno.has(item.sNo));
  }, [analytics.alertRows, filteredRows, activeFilter]);

  const totalPages = useMemo(() => {
    if (!filteredRows.length) return 1;
    return Math.max(1, Math.ceil(filteredRows.length / pageSize));
  }, [filteredRows.length, pageSize]);

  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedRows = useMemo(() => {
    const start = (currentPageSafe - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPageSafe, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const fetchBgDataFinalList = useCallback(async () => {
    if (!departmentId) {
      setError('Department is required to load BG Data Final List.');
      setCanSeePage(true);
      setRows([]);
      return;
    }

    const response = await fileAPI.getBgDataFinalList({ departmentId });
    const nextRows = Array.isArray(response?.data?.rows) ? response.data.rows : [];
    setRows(nextRows);
    setCanSeePage(true);
  }, [departmentId]);

  const loadBgAttachments = useCallback(async () => {
    if (!departmentId) return;
    try {
      const response = await fileAPI.listBgDataAttachments(departmentId);
      const list = Array.isArray(response?.data) ? response.data : [];
      const map = {};
      for (const item of list) {
        const sNo = Number(item.s_no);
        const slot = Number(item.slot_number);
        if (!Number.isFinite(sNo) || slot < 1 || slot > 5) continue;
        if (!map[sNo]) map[sNo] = [null, null, null, null, null];
        map[sNo][slot - 1] = item;
      }
      setAttachmentsBySNo(map);
    } catch (err) {
      // Non-fatal — the main BG table still works without attachment status.
      console.error('Failed to load BG Data attachments:', err);
    }
  }, [departmentId]);

  const handleUploadDoc = useCallback(async (sNo, slot, file) => {
    if (!file || !departmentId) return;
    setDocActionError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      await fileAPI.uploadBgDataAttachment(sNo, slot, formData, departmentId);
      await loadBgAttachments();
    } catch (err) {
      setDocActionError(err?.response?.data?.message || err?.message || 'Failed to upload document.');
    }
  }, [departmentId, loadBgAttachments]);

  const handleRemoveDoc = useCallback(async (id) => {
    if (!departmentId) return;
    const ok = window.confirm('Remove this document?');
    if (!ok) return;
    setDocActionError('');
    try {
      await fileAPI.deleteBgDataAttachment(id, departmentId);
      await loadBgAttachments();
    } catch (err) {
      setDocActionError(err?.response?.data?.message || err?.message || 'Failed to remove document.');
    }
  }, [departmentId, loadBgAttachments]);

  const handleViewDoc = useCallback(async (attachment) => {
    if (!attachment?.id || !departmentId) return;
    setDocActionError('');
    setPreviewLoading(true);
    setShowDocPreview(true);
    try {
      const res = await fileAPI.downloadBgDataAttachment(attachment.id, departmentId);
      const rawBlob = res instanceof Blob
        ? res
        : (res?.data instanceof Blob ? res.data : new Blob([res?.data || ''], { type: attachment.mime_type || 'application/octet-stream' }));
      const url = URL.createObjectURL(rawBlob);
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      setPreviewName(attachment.original_name || 'Document');
      setPreviewMime(attachment.mime_type || rawBlob.type || '');
    } catch (err) {
      setShowDocPreview(false);
      setDocActionError(err?.response?.data?.message || err?.message || 'Failed to load document.');
    } finally {
      setPreviewLoading(false);
    }
  }, [departmentId]);

  const closeDocPreview = useCallback(() => {
    setShowDocPreview(false);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setPreviewName('');
    setPreviewMime('');
  }, []);

  const handleAddFormChange = (field, value) => {
    setAddFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toDateInputValue = (value) => (value ? String(value).slice(0, 10) : '');

  const handleChangeFormFile = (slot, file) => {
    setAddFormFiles((prev) => {
      const next = [...prev];
      next[slot - 1] = file;
      return next;
    });
  };

  const handleEditRow = (row) => {
    setEditingSNo(row.sNo);
    setAddFormData({
      finalProjects: row.finalProjects || '',
      purpose: row.purpose || '',
      bu: row.bu || '',
      eachProject: row.eachProject || '',
      beneficiaryName: row.beneficiaryName || '',
      bgReferenceNo: row.bgReferenceNo || '',
      typeNatureOfBg: row.typeNatureOfBg || '',
      issueDate: toDateInputValue(row.issueDate),
      bank: row.bank || '',
      bgNo: row.bgNo || '',
      amountInr: row.amountInr != null ? String(row.amountInr) : '',
      validUpto: toDateInputValue(row.validUpto),
      claimUpto: toDateInputValue(row.claimUpto)
    });
    setAddFormFiles([null, null, null, null, null]);
    setAddFormError('');
    setAddFormSuccess('');
    setShowAddForm(true);
  };

  const handleCancelEdit = () => {
    setEditingSNo(null);
    setAddFormData(INITIAL_BG_DATA_FINAL_FORM);
    setAddFormFiles([null, null, null, null, null]);
    setAddFormError('');
    setAddFormSuccess('');
    setShowAddForm(false);
  };

  const handleDeleteRow = async (sNo) => {
    const ok = window.confirm(`Delete BG row S No ${sNo}? This also removes its attached documents.`);
    if (!ok) return;
    setError('');
    try {
      await fileAPI.deleteBgDataFinalListRow(sNo, departmentId);
      await fetchBgDataFinalList();
      await loadBgAttachments();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to delete BG Data Final List row.');
    }
  };

  const openImportExcel = () => {
    setImportExcelFile(null);
    setImportExcelResult(null);
    setImportExcelError('');
    setShowImportExcel(true);
  };

  const onImportExcelFileChange = (e) => {
    setImportExcelFile(e.target.files?.[0] || null);
    setImportExcelResult(null);
    setImportExcelError('');
  };

  const onSubmitImportExcel = async () => {
    if (!importExcelFile) {
      setImportExcelError('Please choose an Excel (.xlsx/.xls) file to import.');
      return;
    }
    setImportExcelSubmitting(true);
    setImportExcelError('');
    setImportExcelResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importExcelFile);
      const res = await fileAPI.importBgDataFinalListExcel(formData);
      setImportExcelResult(res?.data || null);
      await fetchBgDataFinalList();
    } catch (err) {
      setImportExcelError(err?.response?.data?.message || err?.message || 'Failed to import Excel file.');
    } finally {
      setImportExcelSubmitting(false);
    }
  };

  const handleCreateBgDataFinalListRow = async (event) => {
    event.preventDefault();
    setAddFormError('');
    setAddFormSuccess('');

    if (!departmentId) {
      setAddFormError('Department context is required.');
      return;
    }

    try {
      setAddFormSubmitting(true);

      let targetSNo = editingSNo;
      if (editingSNo) {
        await fileAPI.updateBgDataFinalListRow(editingSNo, addFormData);
      } else {
        const response = await fileAPI.createBgDataFinalListRow({ departmentId, ...addFormData });
        targetSNo = response?.data?.row?.sNo ?? null;
      }

      if (targetSNo) {
        for (let slot = 1; slot <= 5; slot += 1) {
          const file = addFormFiles[slot - 1];
          if (!file) continue;
          // eslint-disable-next-line no-await-in-loop
          await handleUploadDoc(targetSNo, slot, file);
        }
      }

      setAddFormSuccess(editingSNo ? 'BG Data Final List entry updated successfully.' : 'BG Data Final List entry added successfully.');
      setAddFormData(INITIAL_BG_DATA_FINAL_FORM);
      setAddFormFiles([null, null, null, null, null]);
      setEditingSNo(null);
      await fetchBgDataFinalList();
    } catch (err) {
      setAddFormError(err?.response?.data?.message || err?.message || 'Failed to save BG Data Final List entry.');
    } finally {
      setAddFormSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        await fetchBgDataFinalList();
        await loadBgAttachments();
      } catch (err) {
        const message = err?.message || 'Failed to load BG Data Final List.';
        if (message.includes('403')) {
          setCanSeePage(false);
        } else {
          setError(message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fetchBgDataFinalList, loadBgAttachments]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 mb-0">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <>
      <BGDataFinalListDashboardView
        error={error}
        canSeePage={canSeePage}
        activeFilter={activeFilter}
        onClearFilter={clearFilter}
        kpiCards={kpiCards}
        onApplyFilter={applyFilter}
        analytics={analytics}
        filteredAlertRows={filteredAlertRows}
        rows={rows}
        paginatedRows={paginatedRows}
        currentPageSafe={currentPageSafe}
        pageSize={pageSize}
        filteredRowsCount={filteredRows.length}
        totalPages={totalPages}
        onSetPageSize={setPageSize}
        onSetCurrentPage={setCurrentPage}
        showAddForm={showAddForm}
        setShowAddForm={setShowAddForm}
        addFormError={addFormError}
        addFormSuccess={addFormSuccess}
        addFormData={addFormData}
        onChangeAddFormField={handleAddFormChange}
        onSubmitAddForm={handleCreateBgDataFinalListRow}
        addFormSubmitting={addFormSubmitting}
        onResetAddFormMessages={() => {
          setAddFormError('');
          setAddFormSuccess('');
        }}
        attachmentsBySNo={attachmentsBySNo}
        canManageDocs={canManageDocs}
        docActionError={docActionError}
        onUploadDoc={handleUploadDoc}
        onViewDoc={handleViewDoc}
        onRemoveDoc={handleRemoveDoc}
        editingSNo={editingSNo}
        onCancelEdit={handleCancelEdit}
        addFormFiles={addFormFiles}
        onChangeFormFile={handleChangeFormFile}
        existingDocsForEditing={editingSNo ? (attachmentsBySNo[editingSNo] || [null, null, null, null, null]) : [null, null, null, null, null]}
        canManageRows={canManageRows}
        onEditRow={handleEditRow}
        onDeleteRow={handleDeleteRow}
        onOpenImportExcel={openImportExcel}
        canImportExcel={canImportExcel}
      />

      <Modal show={showImportExcel} onHide={() => setShowImportExcel(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Import BG Data Final List from Excel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted small mb-2">
            Upload a filled copy of the BG Data Final List import template (sheet: &quot;BG Data Final List&quot;).
            To regenerate a blank template, run{' '}
            <code>node backend/scripts/generateBgDataFinalListExcelTemplate.js</code>.
          </div>

          {importExcelError && (
            <Alert variant="danger" dismissible onClose={() => setImportExcelError('')}>
              {importExcelError}
            </Alert>
          )}

          {importExcelResult && (
            <Alert variant="success">
              Import completed — {importExcelResult.created ?? 0} created, {importExcelResult.updated ?? 0} updated,{' '}
              {importExcelResult.skipped ?? 0} row(s) skipped.
              {Array.isArray(importExcelResult.errors) && importExcelResult.errors.length > 0 && (
                <div className="small mt-2" style={{ maxHeight: 180, overflowY: 'auto' }}>
                  {importExcelResult.errors.map((e, idx) => (
                    <div key={`${e.rowNumber}-${idx}`}>
                      Row {e.rowNumber} (S No {e.sNo}): {e.reason}
                    </div>
                  ))}
                </div>
              )}
            </Alert>
          )}

          <Form.Control
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={onImportExcelFileChange}
            disabled={importExcelSubmitting}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImportExcel(false)}>Close</Button>
          <Button variant="primary" onClick={onSubmitImportExcel} disabled={importExcelSubmitting || !importExcelFile}>
            {importExcelSubmitting ? 'Importing…' : 'Import'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDocPreview} onHide={closeDocPreview} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{previewName || 'Document'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewLoading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : (
            <>
              {!previewMime.startsWith('image/') && (
                <div className="d-flex justify-content-end gap-2 mb-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => globalThis.open(previewUrl, '_blank', 'noopener,noreferrer')}
                  >
                    Open in New Tab
                  </Button>
                  <Button size="sm" variant="outline-secondary" as="a" href={previewUrl} download={previewName}>
                    Download
                  </Button>
                </div>
              )}
              {previewMime.startsWith('image/') ? (
                <img src={previewUrl} alt={previewName} style={{ maxWidth: '100%' }} />
              ) : (
                <>
                  <iframe title={previewName} src={previewUrl} style={{ width: '100%', height: '70vh', border: 'none' }} />
                  <p className="text-muted small mt-2 mb-0">
                    If the preview above appears blank, your browser may be configured to block inline document
                    previews — use &quot;Open in New Tab&quot; or &quot;Download&quot; above instead.
                  </p>
                </>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default BGDataFinalListDashboardPage;
